import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { Button, cn } from '@ponzinomics/ui';
import { useAuth } from '../../hooks/use-auth';
import { RELEASE_LOCKS } from '../../lib/release-flags';

type NavIcon = React.ComponentType<{ className?: string }>;

type NavigationLinkItem = {
  name: string;
  href: string;
  icon: NavIcon;
  locked?: boolean;
};

type NavigationGroupItem = {
  name: string;
  items: NavigationLinkItem[];
};

const navigation: Array<NavigationLinkItem | NavigationGroupItem> = [
  {
    name: 'Projects',
    href: '/projects',
    icon: FolderIcon,
  },
  {
    name: 'Website Builder',
    href: '/builder',
    icon: GlobeIcon,
    locked: RELEASE_LOCKS.builder,
  },
  {
    name: 'Ponzinomics',
    items: [
      { name: 'Meme Generator', href: '/ponzinomics/meme', icon: ImageIcon },
      { name: 'Quests', href: '/ponzinomics/quests', icon: TrophyIcon },
      { name: 'Leaderboards', href: '/ponzinomics/leaderboards', icon: ChartIcon },
      // { name: 'Reward Vault', href: '/ponzinomics/lootbox', icon: PackageIcon },
    ],
  },
  {
    name: 'Deploy',
    href: '/token/deploy',
    icon: RocketIcon,
  },

  {
    name: 'Integrations',
    items: [{ name: 'Overview', href: '/integrations', icon: PlugIcon }],
  },
  {
    name: 'Billing',
    href: '/billing',
    icon: CreditCardIcon,
    locked: RELEASE_LOCKS.billing,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: SettingsIcon,
    locked: RELEASE_LOCKS.settings,
  },
];

export function Sidebar(): React.ReactElement {
  const { user, isAuthenticated, isLoading, login, logout, devMode } = useAuth();

  // Get wallet address from Privy localStorage (runs on every render to stay fresh)
  const getWalletFromStorage = (): string | null => {
    try {
      const connections = localStorage.getItem('privy:connections');
      if (connections) {
        const parsed = JSON.parse(connections) as Array<{ address?: string }>;
        return parsed[0]?.address || null;
      }
    } catch {
      // Ignore
    }
    return null;
  };

  const walletAddress = getWalletFromStorage() || user?.walletAddress || null;

  // Format wallet address for display
  const truncatedWallet = walletAddress
    ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
    : null;

  // Display name priority: displayName > email > wallet > fallback
  const displayName = user?.displayName || user?.email || truncatedWallet || 'User';

  return (
    <aside className="flex h-screen w-64 flex-col border-r border-white/[0.06] bg-white/[0.02] backdrop-blur-xl">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b border-white/[0.06] px-6">
        <div className="clip-notched-sm bg-brand-400 glow-brand-sm h-8 w-8" />
        <span className="text-xl font-bold text-white">PONZINOMICS</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 p-3">
        {navigation.map((item) =>
          'items' in item ? (
            <NavGroup key={item.name} name={item.name} items={item.items} />
          ) : (
            <NavItem key={item.href} href={item.href} icon={item.icon} locked={item.locked}>
              {item.name}
            </NavItem>
          )
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-white/[0.06] p-4">
        {isLoading ? (
          <div className="notch-border notch-glass notch-border-white/8 notch-bg-white/3 flex items-center gap-3 p-3">
            <div className="h-8 w-8 animate-pulse rounded-full bg-white/10" />
            <div className="flex-1">
              <div className="h-4 w-20 animate-pulse rounded bg-white/10" />
              <div className="mt-1 h-3 w-16 animate-pulse rounded bg-white/5" />
            </div>
          </div>
        ) : isAuthenticated ? (
          <div className="notch-border notch-glass notch-border-white/8 notch-bg-white/3 flex items-center gap-3 p-3">
            <div className="from-brand-400 to-brand-600 h-8 w-8 rounded-full bg-gradient-to-br" />
            <div className="flex-1 truncate">
              <p className="truncate text-sm font-medium text-white">{displayName}</p>
              <button
                onClick={logout}
                className="text-xs text-gray-500 transition-colors hover:text-red-400"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Button onClick={login} className="w-full" size="sm">
              {devMode ? 'Enter Dev Mode' : 'Connect Wallet'}
            </Button>
            {devMode && (
              <p className="text-center text-xs text-yellow-500">Dev Mode - No Privy configured</p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

interface NavGroupProps {
  name: string;
  items: NavigationLinkItem[];
}

function NavGroup({ name, items }: NavGroupProps): React.ReactElement {
  const [open, setOpen] = useState(true);

  return (
    <div className="pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gray-500 transition-colors hover:text-gray-400"
      >
        <span>{name}</span>
        <svg
          className={cn('h-3 w-3 transition-transform', open ? 'rotate-180' : '')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="mt-1 space-y-0.5 pl-2">
          {items.map((item) => (
            <NavItem key={item.href} href={item.href} icon={item.icon} locked={item.locked}>
              {item.name}
            </NavItem>
          ))}
        </div>
      )}
    </div>
  );
}

interface NavItemProps {
  href: string;
  icon: NavIcon;
  children: React.ReactNode;
  locked?: boolean | undefined;
}

function NavItem({ href, icon: Icon, children, locked = false }: NavItemProps): React.ReactElement {
  if (locked) {
    return (
      <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-gray-500">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span>{children}</span>
        </div>
        <span className="rounded-full border border-orange-400/20 bg-orange-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">
          Soon
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={href}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-400/10 text-brand-400'
            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
        )
      }
    >
      <Icon className="h-5 w-5" />
      {children}
    </NavLink>
  );
}

// Simple icon components
function FolderIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
      />
    </svg>
  );
}

function ImageIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
      />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0"
      />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
      />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
      />
    </svg>
  );
}

function PlugIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
      />
    </svg>
  );
}

function RocketIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
      />
    </svg>
  );
}
